import { useCallback, useEffect, useRef, useState, ReactNode } from 'react';

import { AuthContext, type UserInfo } from './auth-context';
import { createLogger } from '../utils/logger';
import { getTokenExp, isTokenExpired } from '../utils/jwt';
import {
  AUTH_EXPIRED_EVENT,
  AUTH_FORBIDDEN_EVENT,
  fetchCurrentUser,
  type AuthExpiredDetail,
} from '../utils/api';

const log = createLogger('Auth');

const TOKEN_STORAGE_KEY = 'idp_token';
const ROLE_REFRESH_INTERVAL_MS = 60_000;
const authBypassEnabled = import.meta.env.VITE_SKIP_AUTH === 'true';
const bypassUser: UserInfo = {
  id: 'auth-bypass',
  email: 'local-admin@auth-bypass',
  name: 'Local admin',
  role: 'admin',
};

/**
 * Read the token from storage at startup. If the stored token is already
 * expired we wipe it on the spot — this is what stops the "tab open all
 * night" case from looking authenticated to the UI while every API call
 * silently 401's.
 */
function loadInitialToken(): string | null {
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (authBypassEnabled) {
    log.debug('Auth bypass enabled (VITE_SKIP_AUTH=true)');
    return stored;
  }
  if (!stored) {
    log.debug('No stored auth token, user is unauthenticated');
    return null;
  }
  if (isTokenExpired(stored)) {
    log.info('Stored auth token has expired; clearing');
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
  log.debug('Restored auth token from storage');
  return stored;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(loadInitialToken);
  const [user, setUser] = useState<UserInfo | null>(
    authBypassEnabled ? bypassUser : null
  );
  const [roleLoading, setRoleLoading] = useState(
    () => !authBypassEnabled && Boolean(token)
  );
  const profileRequest = useRef(0);

  const isAuthenticated =
    authBypassEnabled || (!!token && !isTokenExpired(token));

  const login = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setToken(newToken);
    log.info('User logged in');
  }, []);

  const logout = useCallback(() => {
    profileRequest.current += 1;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken((prev) => (prev == null ? prev : null));
    setUser(null);
    setRoleLoading(false);
    log.info('User logged out');
  }, []);

  const refreshUser = useCallback(async () => {
    if (authBypassEnabled) {
      setUser(bypassUser);
      setRoleLoading(false);
      return;
    }
    if (!token || isTokenExpired(token)) {
      setUser(null);
      setRoleLoading(false);
      return;
    }

    const request = ++profileRequest.current;
    setRoleLoading(true);
    try {
      const profile = await fetchCurrentUser();
      if (request === profileRequest.current) {
        setUser(profile);
      }
    } catch (error) {
      // Leave the role unknown rather than assuming `regular`: a users
      // endpoint that is down must never strip controls from an admin.
      if (request === profileRequest.current) {
        setUser(null);
        log.error(
          `Failed to load user role: ${
            error instanceof Error ? error.message : 'unknown error'
          }`
        );
      }
    } finally {
      if (request === profileRequest.current) {
        setRoleLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    if (authBypassEnabled || !isAuthenticated) return;
    const timer = window.setInterval(() => {
      void refreshUser();
    }, ROLE_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, refreshUser]);

  // Schedule an automatic logout exactly when the JWT's `exp` is reached so
  // the UI flips to the login screen the moment the session becomes useless,
  // instead of waiting for the next API call to fail.
  useEffect(() => {
    if (authBypassEnabled || !token) return;
    const exp = getTokenExp(token);
    if (exp == null) return;

    const msUntilExpiry = exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      logout();
      return;
    }

    log.debug(
      `Token expires in ${Math.round(msUntilExpiry / 1000)}s; scheduling auto-logout`
    );
    const timer = window.setTimeout(() => {
      log.info('Token expired; logging out');
      logout();
    }, msUntilExpiry);
    return () => window.clearTimeout(timer);
  }, [token, logout]);

  // React to logout signals coming from the API layer (401 responses or
  // an expired-token pre-flight in `apiFetch`).
  useEffect(() => {
    if (authBypassEnabled) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<AuthExpiredDetail>).detail;
      log.info(`Auth-expired event received (${detail?.reason ?? 'unknown'})`);
      logout();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, [logout]);

  // An admin session can be demoted from another browser. When a request is
  // then refused, re-read the role so the controls disappear without a reload.
  useEffect(() => {
    if (authBypassEnabled) return;
    const handler = () => {
      void refreshUser();
    };
    window.addEventListener(AUTH_FORBIDDEN_EVENT, handler);
    return () => window.removeEventListener(AUTH_FORBIDDEN_EVENT, handler);
  }, [refreshUser]);

  // Background timers can be throttled or paused while the tab is hidden, so
  // re-check the token whenever the user returns to the tab. This is the
  // common "left it open overnight, came back in the morning" path.
  useEffect(() => {
    if (authBypassEnabled) return;
    const recheck = () => {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (stored && isTokenExpired(stored)) {
        log.info('Tab refocused with expired token; logging out');
        logout();
        return;
      }
      if (stored) void refreshUser();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', recheck);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', recheck);
    };
  }, [logout, refreshUser]);

  const role = user?.role ?? null;

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        authBypassActive: authBypassEnabled,
        user,
        role,
        isAdmin: role === 'admin',
        canDestroy: role !== 'regular',
        roleLoading,
        refreshUser,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
